import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import moment from 'moment';
import SingleDateCalendar from '../SingleDateCalendar';
import { 
  DATA_SUMMARY_SECTION_I18N_KEYS as MY_I18N_KEYS, 
  DATA_SUMMARY_SECTION_I18N_PREFIX as MY_I18N_PREFIX 
} from '../constants/i18n/DataSummarySectioni18nConstants';
import {
  SOURCE_KEY,
  CONTENT_KEY,
  DATE_KEY,
  DATE_TIME_IN_YYYY_MM_DD_FORMAT,
  MAXIMUM_FILE_SIZE_IN_KB,
  MAXIMUM_NUMBER_OF_ROWS_IN_UPLOADED_FILE
} from '../constants/constants';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-daterangepicker/daterangepicker.css';

import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';


const options = [
  { value: 'english', label: 'EN: English' },
  { value: 'simplified_chinese', label: 'ZH: Simplified Chinese' },
  { value: 'malay', label: 'MS: Bahasa Melayu' },
  { value: 'bahasa_indonesia', label: 'ID: Bahasa Indonesia' },
];

class DataSummarySection extends Component {
  static propTypes = {
    fileInfoAfterValidation:                    PropTypes.object.isRequired,
    includeMissingValue:                        PropTypes.bool.isRequired,
    totalDataPointsForUploadedFile:             PropTypes.number.isRequired,
    displayLanguageSelection:                   PropTypes.bool.isRequired,
    handleUserInputDateForUploadedComments:     PropTypes.func,
    handleUserInputLanguageForUploadedComments: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedDate: moment().format(DATE_TIME_IN_YYYY_MM_DD_FORMAT),
      selectedLanguage: options[0]
    };
  }

  componentDidMount() {
    const { 
      includeMissingValue, 
      fileInfoAfterValidation 
    } = this.props;
    const hasDateColumn = fileInfoAfterValidation.columns && fileInfoAfterValidation.columns.includes(DATE_KEY);

    if (!includeMissingValue && !hasDateColumn && this.props.handleUserInputDateForUploadedComments) {
      this.props.handleUserInputDateForUploadedComments(this.state.selectedDate);
    }
    if (this.props.handleUserInputLanguageForUploadedComments) {
      this.props.handleUserInputLanguageForUploadedComments(this.state.selectedLanguage.value);
    }
  }

  getI18nText(textKey, params = {}) {
    return t(`${MY_I18N_PREFIX}.${textKey}`, params);
  }

  handleDateChange = (date) => {
    this.props.handleUserInputDateForUploadedComments(date);
    this.setState({
      selectedDate: date
    });
  };

  handleLanguageChange = (option) => {
    this.props.handleUserInputLanguageForUploadedComments(option.value);
    this.setState({ 
      selectedLanguage: option 
    });
  };

  renderFileSize() {
    const { fileInfoAfterValidation } = this.props;
    if (fileInfoAfterValidation.size <= MAXIMUM_FILE_SIZE_IN_KB) {
      return (
        <span className="col-cell">{this.getI18nText(MY_I18N_KEYS.kb_label, { fileSizeInKB: fileInfoAfterValidation.size })}</span>
      );
    }
    else {
      return (
        <span className="col-error-text">
          {this.getI18nText(MY_I18N_KEYS.kb_label, { fileSizeInKB: fileInfoAfterValidation.size })}
          {this.getI18nText(MY_I18N_KEYS.file_size_exceeded_message)}
        </span>
      );
    }
  }

  renderTotalDataPoints() {
    const { totalDataPointsForUploadedFile } = this.props;
    if (totalDataPointsForUploadedFile <= MAXIMUM_NUMBER_OF_ROWS_IN_UPLOADED_FILE) {
      return (
        <span className="col-cell">{totalDataPointsForUploadedFile}</span>
      );
    }
    else {
      return (
        <span className="col-error-text">
          {totalDataPointsForUploadedFile}
          {this.getI18nText(MY_I18N_KEYS.rows_exceeded_message)}
        </span>
      );
    }
  }
  
  renderRemarks(hasContentColumn, hasSourceColumn) {
    if (!hasContentColumn) {
      return <em className="col-error-text">{this.getI18nText(MY_I18N_KEYS.no_content_column_detected_remarks)}</em>;
    } else if (!hasSourceColumn) {
      return <em>{this.getI18nText(MY_I18N_KEYS.no_source_remarks)}</em>;
    }
    return null;
  }

  render() {
    const { 
      fileInfoAfterValidation, 
      includeMissingValue, 
      totalDataPointsForUploadedFile,
      displayLanguageSelection 
    } = this.props;
    const { selectedDate, selectedLanguage } = this.state;

    const hasSourceColumn = fileInfoAfterValidation.columns && fileInfoAfterValidation.columns.includes(SOURCE_KEY);
    const hasContentColumn = fileInfoAfterValidation.columns && fileInfoAfterValidation.columns.includes(CONTENT_KEY);
    const hasDateColumn = fileInfoAfterValidation.columns && fileInfoAfterValidation.columns.includes(DATE_KEY);
    
    return (
      <div className='modal-body data-summary-modal-body'>
        <div className="data-summary-row">
          <div className='col-header'>
            <span>{this.getI18nText(MY_I18N_KEYS.file_name)}</span>
          </div>
          <span className="col-cell">{fileInfoAfterValidation.name}</span>
        </div>
        <div className="data-summary-row">
          <div className='col-header'>
            <span>{this.getI18nText(MY_I18N_KEYS.file_size)}</span>
          </div>
          {this.renderFileSize()}
        </div>
        <div className="data-summary-row">
          <div className='col-header'>
            <span>{this.getI18nText(MY_I18N_KEYS.columns)}</span>
          </div>
          <span className="col-cell">
          {totalDataPointsForUploadedFile > 0 ? fileInfoAfterValidation.columns.join(' | ') : (
              <span className="col-error-text">{this.getI18nText(MY_I18N_KEYS.no_relevant_columns_detected_remarks)}</span>
            )}
            {totalDataPointsForUploadedFile > 0 && (
              <div className="col-remarks">
                {this.renderRemarks(hasContentColumn, hasSourceColumn)}
              </div>
            )}
          </span>
        </div>
        <div className="data-summary-row">
          <div className='col-header'>
            <span>
              {this.getI18nText(MY_I18N_KEYS.total_data_points)}
              {includeMissingValue && (
                <div className="col-remarks">
                  <em>{this.getI18nText(MY_I18N_KEYS.includes_missing_data_remarks)}</em>
                </div>
              )}
            </span>
          </div>
          {this.renderTotalDataPoints()}
        </div>
        {!includeMissingValue && !hasDateColumn && (
          <div className="data-summary-row">
            <div className='col-header'>
              <span>{this.getI18nText(MY_I18N_KEYS.date_for_all_data_points)}</span>
            </div>
            <span className="col-cell">
              <div className="data-summary-calendar-wrapper">
                <SingleDateCalendar
                  earliestHistoricalDataDate = {selectedDate} 
                  setEarliestHistoricalDataDate = {e => this.handleDateChange(e)}
                  drops="down"
                  showRanges={false}
                />
              </div>
              <div className="col-remarks">
                <em>{this.getI18nText(MY_I18N_KEYS.no_date_column_detected_remarks)}</em>
              </div>
            </span>
          </div>
        )}
        {displayLanguageSelection && (<div className="data-summary-row">
          <div className='col-header'>
            <span>{this.getI18nText(MY_I18N_KEYS.language)}</span>
          </div>
          <span className="col-cell">
            <Dropdown
              placeholder={selectedLanguage.label}
              options={options}
              onChange={this.handleLanguageChange}
              className="custom-dropdown"
              controlClassName="custom-dropdown-control"
            />
          </span>
        </div>)}
      </div>
    );
  }
}

export default DataSummarySection;



// WEBPACK FOOTER //
// ./src/features/topics/components/file-upload/DataSummarySection.js